import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useNewsletterApi } from '../../hooks/useNewsletterApi';
import { useSnackbar } from '../Stateless/SnackBarProvider';
import { 
    SendNewsletterRequest, 
    DeleteNewsletterSubscribersRequest,
    GetNewsletterSubscribersLimitEnum,
    GetNewsletterSubscribersStatusEnum,
    GetNewsletterSubscribersSortByEnum,
    GetNewsletterSubscribersSortOrderEnum,
    ToggleNewletterSubscriberStatusRequest
} from '../../client-sdk';

type NewsletterSubscribersParams = {
    page: number;
    limit: GetNewsletterSubscribersLimitEnum;
    search?: string;
    status?: GetNewsletterSubscribersStatusEnum;
    sortBy?: GetNewsletterSubscribersSortByEnum;
    sortOrder?: GetNewsletterSubscribersSortOrderEnum;
};

export const useGetNewsletterSubscribers = (
    queryParams: NewsletterSubscribersParams, 
    options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
    const { getNewsletterSubscribers } = useNewsletterApi();
    
    const {
        data,
        isLoading: isFetching,
        refetch,
        isRefetching,
    } = useQuery({
        queryKey: ['getNewsletterSubscribers', queryParams],
        queryFn: () => {
            return getNewsletterSubscribers(queryParams);
        },
        ...options,
    });

    const isLoading = isFetching || isRefetching;

    return {
        data,
        isLoading,
        refetch,
    };
};

export const useSendNewsletter = () => {
    // const queryClient = useQueryClient();
    const { sendNewsletter } = useNewsletterApi();
    const { showMessage } = useSnackbar();

    return useMutation({
        mutationKey: ['sendNewsletter'],
        mutationFn: (requestData: SendNewsletterRequest) => {
            return sendNewsletter(requestData);
        },
        onSuccess: (response: any) => {
            showMessage(
                response?.message || 'Newsletter sent successfully',
                'success'
            );
        },
        onError: (error: any) => {
            showMessage(
                error.response?.data?.message || 'Failed to send newsletter',
                'error'
            );
        },
    });
};

export const useDeleteNewsletterSubscribers = () => {
    const queryClient = useQueryClient();
    const { deleteNewsletterSubscribers } = useNewsletterApi();
    const { showMessage } = useSnackbar();

    return useMutation({
        mutationKey: ['deleteNewsletterSubscribers'],
        mutationFn: (requestData: DeleteNewsletterSubscribersRequest) => {
            return deleteNewsletterSubscribers(requestData);
        },
        onSuccess: (response: any) => {
            showMessage(
                response?.message || 'Newsletter subscribers deleted successfully',
                'success'
            );
            queryClient.invalidateQueries({
                queryKey: ['getNewsletterSubscribers'],
                exact: false,
            });
        },
        onError: (error: any) => {
            showMessage(
                error.response?.data?.message || 'Failed to delete newsletter subscribers',
                'error'
            );
        },
    });
};

export const useToggleNewsletterSubscribersStatus = () => {
    const queryClient = useQueryClient();
    const { toggleNewsletterSubscribersStatus } = useNewsletterApi();
    const { showMessage } = useSnackbar();

    return useMutation({
        mutationKey: ['toggleNewsletterSubscribersStatus'],
        mutationFn: (requestData: ToggleNewletterSubscriberStatusRequest) => {
            return toggleNewsletterSubscribersStatus(requestData);
        },
        onSuccess: (response: any) => {
            showMessage(
                response?.message || 'Subscriber status updated successfully',
                'success'
            );
            queryClient.invalidateQueries({
                queryKey: ['getNewsletterSubscribers'],
                exact: false,
            });
        },
        onError: (error: any) => {
            showMessage(
                error.response?.data?.message || 'Failed to update subscriber status',
                'error'
            );
        },
    });
};
